import { OnboardingCell } from './coachOnboarding';

/**
 * 시안 **상태 A · 상태 B** — 강사 0명, 세팅 완료 (정책 §6).
 *
 * 두 상태는 강사 카드가 한 장도 없거나(A) 전부 끝난(B) 화면이라, 카드
 * 컴포넌트로는 만들 수 없다. 시안이 「이 두 상태도 구현이 필요합니다」라고
 * 따로 적어 둔 이유다.
 */

const LINE = '#C9D9EE';
const CELL_BG = '#F7F9FC';
const CELL_LINE = '#EDF1F7';
const INK = '#16202E';
const MUTE = '#7C8794';

const Shell = ({
    rail,
    tag,
    tagTone,
    title,
    head,
    unlock,
    cells,
    label,
}: {
    rail: string;
    tag: string;
    tagTone: { bg: string; fg: string };
    title: string;
    head: string;
    unlock?: string;
    cells: OnboardingCell[];
    label: string;
}) => (
    <div
        className="flex w-full overflow-hidden rounded-[16px] border border-solid"
        style={{ borderColor: LINE, background: '#FFFFFF', marginBottom: 14 }}
        aria-label={label}
    >
        <div style={{ width: 6, flex: 'none', background: rail }} />
        <div className="flex min-w-0 flex-1 flex-col" style={{ padding: '18px 20px' }}>
            <div className="flex flex-wrap items-center" style={{ gap: 9 }}>
                <span
                    className="rounded-[6px]"
                    style={{
                        background: tagTone.bg,
                        color: tagTone.fg,
                        fontSize: 12.5,
                        fontWeight: 800,
                        padding: '4px 10px',
                        flex: 'none',
                    }}
                >
                    {tag}
                </span>
                <span style={{ fontSize: 16, fontWeight: 800, color: INK, wordBreak: 'keep-all' }}>
                    {title}
                </span>
            </div>
            <div
                style={{
                    fontSize: 19,
                    fontWeight: 800,
                    color: INK,
                    letterSpacing: '-0.35px',
                    marginTop: 11,
                    wordBreak: 'keep-all',
                }}
            >
                {head}
            </div>
            {unlock && (
                <div
                    className="rounded-[10px]"
                    style={{
                        marginTop: 12,
                        background: '#E6F5EF',
                        color: '#0E7C58',
                        fontSize: 13,
                        fontWeight: 700,
                        padding: '9px 13px',
                        wordBreak: 'keep-all',
                    }}
                >
                    🔓 {unlock}
                </div>
            )}
            {/* 시안 `.cells` — 기본 한 줄, 1100px 미만에서만 3+3 (CoachOnboardingCard 와 같은 규칙) */}
            <div
                className="flex flex-nowrap overflow-hidden rounded-[12px] max-[1100px]:flex-wrap"
                style={{ gap: 1, background: CELL_LINE, border: `1px solid ${CELL_LINE}`, marginTop: 14 }}
            >
                {cells.map((c) => (
                    <div
                        key={c.k}
                        className="flex min-w-0 flex-1 flex-col items-center max-[1100px]:basis-[32%]"
                        style={{
                            background: c.tone === 'off' ? '#F3F5F9' : CELL_BG,
                            padding: '11px 8px',
                        }}
                    >
                        <span
                            className="w-full overflow-hidden text-ellipsis"
                            title={c.k}
                            style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: c.tone === 'off' ? '#A8B3C4' : MUTE,
                                whiteSpace: 'nowrap',
                                textAlign: 'center',
                            }}
                        >
                            {c.k}
                        </span>
                        <span
                            className="flex max-w-full items-baseline justify-center overflow-hidden"
                            style={{
                                gap: 2,
                                marginTop: 4,
                                fontSize: c.tone === 'off' ? 13 : 18,
                                fontWeight: 800,
                                color:
                                    c.tone === 'off'
                                        ? '#A8B3C4'
                                        : c.tone === 'good'
                                          ? '#0E7C58'
                                          : c.tone === 'hot'
                                            ? '#D22730'
                                            : INK,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {c.v}
                            {c.unit && (
                                <span style={{ fontSize: 12, fontWeight: 700, color: MUTE }}>{c.unit}</span>
                            )}
                        </span>
                        {c.s && (
                            <span style={{ fontSize: 11, fontWeight: 700, color: MUTE, marginTop: 2 }}>
                                {c.s}
                            </span>
                        )}
                    </div>
                ))}
            </div>
        </div>
    </div>
);

/**
 * 상태 A — **강사 0명.** 이 카드 하나만 보인다 (정책 §6).
 *
 * 🔴 뒤 단계를 전부 「등록 후」로 잠근다. 강사가 없으면 배정도 수강권도
 *    물어볼 수 없다 — 빈 숫자를 0 으로 그리면 「다 했다」로 읽힌다.
 */
export const NoCoachCard = () => (
    <Shell
        rail="#E8A93C"
        tag="원장님이 할 일"
        tagTone={{ bg: '#DCE7F8', fg: '#2C4C8C' }}
        title="아직 등록된 강사가 없습니다"
        head="먼저 강사를 등록해 주세요"
        unlock="강사가 등록되면 — 회원 배정 · 수업 · 분석이 순서대로 열립니다"
        label="강사 온보딩 · 강사 0명"
        cells={[
            { k: '등록 강사', v: '0', unit: '명', tone: 'hot' },
            { k: '센터 수락', v: '등록 후', tone: 'off' },
            { k: '담당 회원', v: '등록 후', tone: 'off' },
            { k: '수강권', v: '등록 후', tone: 'off' },
            { k: '이번 주 수업', v: '등록 후', tone: 'off' },
            { k: '일지 · 최근 7일', v: '등록 후', tone: 'off' },
        ]}
    />
);

/**
 * 상태 B — **세팅 완료 졸업 카드.** 하루 한 번 보여주고 접는다 (정책 §6).
 *
 * ⚠️ 앱 다운·일지는 **종료 조건이 아니다.** 5단계가 끝나도 남아 있는 값이라
 *    「계속 챙깁니다」로 적는다 — 초록으로 칠해 「끝났다」고 하면 거짓말이 된다.
 */
export const GraduationCard = ({
    coaches,
    members,
    withPass,
    analyzing,
    appMissing,
    journal,
}: {
    coaches: number;
    members: number;
    withPass: number | null;
    analyzing: number;
    appMissing: number | null;
    journal: { written: number; total: number } | null;
}) => (
    <Shell
        rail="#3EBD8B"
        tag="세팅 완료"
        tagTone={{ bg: '#E6F5EF', fg: '#0E7C58' }}
        title="오늘 하루만 보여드립니다"
        head="🎉 센터 세팅이 끝났습니다 — 내일부터 회원 분석 브리핑이 시작됩니다"
        label="강사 온보딩 · 세팅 완료"
        cells={[
            { k: '강사', v: String(coaches), unit: '명 연동', tone: 'good' },
            { k: '회원', v: String(members), unit: '명 배정', tone: 'good' },
            withPass == null
                ? { k: '수강권', v: '확인 중', tone: 'off' }
                : { k: '수강권', v: String(withPass), unit: `/${members}명`, tone: 'good' },
            { k: '분석 시작', v: String(analyzing), unit: '명', tone: 'good' },
            appMissing == null
                ? { k: '앱 다운 안 함', v: '확인 중', tone: 'off' }
                : {
                      k: '앱 다운 안 함',
                      v: String(appMissing),
                      unit: '명',
                      s: '계속 챙깁니다',
                      tone: 'plain',
                  },
            journal == null
                ? { k: '일지 · 최근 7일', v: '확인 중', tone: 'off' }
                : {
                      k: '일지 · 최근 7일',
                      v: String(journal.written),
                      unit: `/${journal.total}건`,
                      s: '계속 챙깁니다',
                      tone: 'plain',
                  },
        ]}
    />
);
